import { Box, Button } from '@mui/material';
import type { ReactNode } from 'react';
import { ContentSkeleton, type ContentSkeletonVariant } from './ContentSkeleton';
import { ResultState, type ResultStateProps } from './ResultState';

export type ResourceStateProps = {
  loading: boolean;
  /** `undefined`만 오류 없음으로 해석하며, null이나 빈 문자열도 명시적인 오류 상태입니다. */
  error?: ReactNode;
  errorTitle?: ReactNode;
  empty: boolean;
  onRetry?: () => void;
  /** 스켈레톤의 접근 가능한 이름으로만 사용하며 화면에는 표시하지 않습니다. */
  loadingLabel?: ReactNode;
  loadingVariant?: ContentSkeletonVariant;
  loadingRows?: number;
  loadingColumns?: number;
  loadingRowHeight?: number;
  retryLabel?: ReactNode;
  emptyTitle?: ReactNode;
  emptyDescription?: ReactNode;
  errorAction?: ReactNode;
  emptyAction?: ReactNode;
  embedded?: boolean;
  /** 정상 본문과 로딩 행은 유지하고, 오류·빈 상태의 표시 밀도만 지정합니다. */
  density?: ResultStateProps['density'];
  /** 세로로 배치한 페이지에서 로딩·오류·빈 상태가 남은 공간을 채웁니다. 정상 본문은 늘리지 않습니다. */
  fill?: boolean;
  children: ReactNode;
};

export function ResourceState({
  loading,
  error,
  errorTitle = '정보를 불러오지 못했습니다.',
  empty,
  onRetry,
  loadingLabel = '데이터를 불러오는 중입니다.',
  loadingVariant = 'list',
  loadingRows,
  loadingColumns,
  loadingRowHeight,
  retryLabel = '다시 시도',
  emptyTitle = '표시할 데이터가 없습니다.',
  emptyDescription,
  errorAction,
  emptyAction,
  embedded = false,
  density = 'standard',
  fill = false,
  children,
}: ResourceStateProps) {
  const hasError = error !== undefined;
  const stateContent = (content: ReactNode) => fill
    ? <Box sx={{ flex: 1, minHeight: 0 }}>{content}</Box>
    : content;
  if (loading) {
    return stateContent(<ContentSkeleton variant={loadingVariant} label={loadingLabel} rows={loadingRows} columns={loadingColumns} rowHeight={loadingRowHeight} embedded={embedded} />);
  }
  if (hasError) {
    return stateContent(<ResultState tone="danger" title={errorTitle} description={error} action={errorAction ?? (onRetry ? <Button variant="weak" onClick={onRetry}>{retryLabel}</Button> : null)} embedded={embedded} density={density} />);
  }
  if (empty) {
    return stateContent(<ResultState title={emptyTitle} description={emptyDescription} action={emptyAction ?? (onRetry ? <Button size="small" variant="weak" onClick={onRetry}>새로고침</Button> : null)} embedded={embedded} density={density} />);
  }
  return children;
}
